import { IHttpResponse, IRootScopeService, ITimeoutService, IWindowService } from 'angular';
import { LocalStorageService } from 'Src/ng2/shared/services/web-storage/local-storage/local-storage.service';

export class PortalLastUpdatedInterceptorService {
  static $inject: string[] = ['PublicConfig', 'LocalStorageService', '$rootScope', '$window', '$timeout'];

  // values store in local storate (CM).
  static LAST_UPDATED = 'lastUpdated';
  static APP_UPDATE_AVAILABLE = 'appUpdateAvailable';

  // name of response header we are intercepting (CM).
  static HEADER_NAME = 'x-portal-last-updated';

  public response: (config: IHttpResponse<any>) => IHttpResponse<any>;

  constructor(
    private PublicConfig,
    private localStorageService: LocalStorageService,
    private $rootScope: IRootScopeService,
    private $window: IWindowService,
    private $timeout: ITimeoutService,
  ) {
    // Checks if a new version of the app has been deployed on `$stateChangeSuccess`.
    // We use the `$stateChangeSuccess` event because it is triggered after the response
    // from the server notifying that there is a new version of the app has been received.
    // If there is a new version of the app, we force a page reload.
    // We do the page reload on transitions so that the reload is unobtrusive to the user (CM).
    this.$rootScope.$on('$stateChangeSuccess', (event, toState, toParams, fromState) => {
      const appUpdateAvailable = this.isAppUpdateAvailable();
      // Ignore refresh if the `toState` is the `callback` state to avoid issues with
      // the url parsing to obtain tokens that auth0 does on login.
      // In the scenario where `toState` is `callback`, the refresh will happen on the redirect
      // from the `callback` state to the new state (CM).
      const toStateIsCallback = toState ? toState.name === 'callback' : false;

      if (appUpdateAvailable && !toStateIsCallback) {
        this.setAppUpdateAvailable(false);
        this.refreshBrowser(); // force refresh of page (CM).
      }
    });

    // AngularJS interceptors are meant to be factories; hence, we add the response configuration inside of the
    // constructor so that we can use a class and lock down the context of `this` using an arrow function (CM).
    this.response = config => {
      if (config && config.headers) {
        const headerValIntercepted = config.headers(PortalLastUpdatedInterceptorService.HEADER_NAME);

        if (headerValIntercepted) {
          const headerValStored = this.getHeaderValStored();

          if (headerValStored) {
            const headerValChanged = headerValStored !== headerValIntercepted;

            if (headerValChanged) {
              const appUpdateAvailable = true;

              this.setHeaderValStored(headerValIntercepted);
              this.setAppUpdateAvailable(appUpdateAvailable);
            }
          } else {
            this.setHeaderValStored(headerValIntercepted);
          }
        }

        return config;
      }
    };
  }

  // This method was made public so that it could be unit tested (CM).
  public refreshBrowser(): void {
    // $timeout is used to wait until the next digest cycle to refresh the browser
    // waiting until the next digest cycle ensures, that the page transition has fully taken place before refreshing (CM)
    this.$timeout(() => {
      this.$window.location.reload();
    });
  }

  private getHeaderValStored(): string {
    const lastUpdated = this.localStorageService.getItem(PortalLastUpdatedInterceptorService.LAST_UPDATED);

    return lastUpdated;
  }

  private isAppUpdateAvailable(): boolean {
    let appUpdateAvailable: any = this.localStorageService.getItem(
      PortalLastUpdatedInterceptorService.APP_UPDATE_AVAILABLE,
    );

    if (!appUpdateAvailable) {
      appUpdateAvailable = false;
      this.setAppUpdateAvailable(appUpdateAvailable);
    }

    return appUpdateAvailable;
  }

  private setAppUpdateAvailable(appUpdateAvailable: boolean): void {
    this.localStorageService.setItem(PortalLastUpdatedInterceptorService.APP_UPDATE_AVAILABLE, appUpdateAvailable);
  }

  private setHeaderValStored(lastUpdatedVal: string): void {
    this.localStorageService.setItem(PortalLastUpdatedInterceptorService.LAST_UPDATED, lastUpdatedVal);
  }
}
